import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, ContextMenuItem, IconButton, Modal, SystemIcon, TextField, Typography } from '@volvo/vcdk-react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import type { SystemIconId } from '@volvo/vcdk-assets';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import { queryKeys } from '@/service/vpc/queryOptions';
import { vpcService } from '@/service/vpc/vpcService';
import { fleet } from '@/utils';
import { cn } from '@/utils/cn';

type Props = {
  name: string;
  fleetId: string;
  entityVersion: number;
};

const DeleteFleetModal: React.FC<Props> = ({ name, fleetId, entityVersion }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { fleetAccountId } = useFleetAccount();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const iconsList: SystemIconId[] = [
    'vessel',
    'ferry',
    'deforestation-line',
    'small-crusher',
    'bus-side-city',
    'scraper',
    'terminal-tractor-front',
    'reach-stacker',
    'articulated-haulers',
    'bus-side-coach',
    'excavator',
    'paver',
    'skid-steer-loader',
    'demolition',
    'track-loader',
    'compact-excavator',
    'excavator-on-wheels',
    'wheel-loader',
    'terminal-tractor',
    'pipelayer',
    'forklift',
    'dozer',
    'truck-timber',
    'truck-heavy',
    'truck-box',
    'truck-side',
    'cargo-ship',
    'forest-machinery',
    'rigid-haulers',
    'engine-penta',
  ];

  const [icon, setIcon] = useState<SystemIconId>(fleet.parseFleetName(name).icon);
  const [fleetName, setFleetName] = useState<string>(fleet.parseFleetName(name).fleetName);

  const { mutateAsync: editFleet, isPending } = useMutation({
    mutationFn: (props: { fleetAccountId: string; fleetId: string; name: string; entityVersion: number }) => {
      return vpcService.Fleet.editFleet(props);
    },
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.getFleets], exact: false });
      setIsOpen(false);
    },
  });

  return (
    <>
      <ContextMenuItem disabled={isPending} onClick={() => setIsOpen(true)}>
        {t(($) => $.pages.account.fleet_overview.edit)}
      </ContextMenuItem>
      {isOpen &&
        createPortal(
          <Modal open={isOpen} onClose={() => setIsOpen(false)} width={700} maxWidth="90%">
            <span slot="header">{t(($) => $.components.modal.edit_fleet_modal.title)}</span>
            <div className="flex flex-col gap-8">
              <div className="flex items-end gap-8">
                <div className="bg-gray-light flex size-12 items-center justify-center rounded">
                  <SystemIcon icon={icon} size={42} />
                </div>
                <div className="flex-1">
                  <TextField
                    label={t(($) => $.components.modal.edit_fleet_modal.fleet_name)}
                    value={fleetName}
                    maxLength={50}
                    onInput={(e) => setFleetName(e.detail.value.slice(0, 50))}
                  />
                </div>
              </div>
              <div>
                <Typography variant="subtitle2">{t(($) => $.components.modal.edit_fleet_modal.select_icon)}</Typography>
                <div className="bg-neutral-light flex flex-wrap gap-4 rounded px-7 py-5">
                  {iconsList.map((iconItem) => (
                    <IconButton
                      key={iconItem}
                      variant="secondary"
                      icon={iconItem}
                      size="large"
                      onClick={() => setIcon(iconItem)}
                      className={cn({
                        'bg-neutral': icon === iconItem,
                      })}
                      aria-pressed={icon === iconItem}
                    />
                  ))}
                </div>
              </div>
            </div>
            <span slot="footer">
              <div className="flex gap-4">
                <Button variant="secondary" size="medium" disabled={isPending} onClick={() => setIsOpen(false)}>
                  {t(($) => $.common.cancel)}
                </Button>
                <Button
                  variant="primary"
                  size="medium"
                  loading={isPending}
                  onClick={() =>
                    editFleet({
                      fleetAccountId: fleetAccountId!,
                      fleetId,
                      name: `::${icon}::${fleetName}`,
                      entityVersion,
                    })
                  }
                >
                  {t(($) => $.common.save_changes)}
                </Button>
              </div>
            </span>
          </Modal>,
          document.body
        )}
    </>
  );
};

export default withComponentErrorBoundary(DeleteFleetModal);
